import { type PropsWithChildren, createContext, useContext } from 'react';

import type {
  AccordionReducerActions,
  AccordionState,
} from '../accordion-state.js';

type AccordionContextProps = {
  triggerPosition: 'start' | 'end';
  interactive: boolean;
  color: 'neutral' | 'primary' | 'success' | 'warning' | 'critical';
  size: 'default' | 'condensed';
  state?: AccordionState;
  dispatch?: (action: AccordionReducerActions) => void;
};

const AccordionContext = createContext<AccordionContextProps>({
  triggerPosition: 'end',
  interactive: true,
  color: 'neutral',
  size: 'default',
  state: undefined,
  dispatch: undefined,
});

/** @internal */
export function AccordionContextProvider(
  props: PropsWithChildren<{ value: AccordionContextProps }>,
) {
  return (
    <AccordionContext.Provider value={props.value}>
      {props.children}
    </AccordionContext.Provider>
  );
}

/** @internal */
export function useAccordionContext() {
  const { triggerPosition, interactive, color, size, state, dispatch } =
    useContext(AccordionContext);

  if (state === undefined || dispatch === undefined) {
    throw new Error(
      'Accordion context is used outside of an Accordion. Please make sure you are using the correct Accordion components.',
    );
  }

  return {
    triggerPosition,
    interactive,
    color,
    size,
    state,
    dispatch,
  };
}
